<template>
  <div class="salary-page">
    <section class="hero-section">
      <div class="page-meta">
        <!--<span class="meta-city">{{ cityName || '--' }}</span>-->
        <span class="meta-team">{{ teamName || '--' }}</span>
      </div>
    </section>

    <div class="page-body">
      <section class="filter-card">
        <button type="button" class="filter-main" @click="openDatePopup">
          <div class="filter-date-line">
            <div class="filter-date">{{ currentDateLabel }}</div>
            <div class="filter-tip">选择日期</div>
          </div>
        </button>
        <div class="filter-actions">
          <button class="scheme-button" type="button" @click="openScheme">
            薪资方案
          </button>
        </div>
      </section>

      <section class="summary-card">
        <div class="summary-row summary-row-top">
          <div class="summary-meta summary-meta-inline">
            <span class="summary-label">发薪时间</span>
            <span class="summary-date">{{ currentDateLabel }}</span>
          </div>
          <button class="summary-toggle" type="button" @click="toggleAmountVisible">
            {{ isAmountVisible ? '隐藏显示' : '显示金额' }}
          </button>
        </div>
        <div class="summary-row summary-row-bottom">
          <div class="summary-metric">
            <div class="summary-title">总计建议薪资</div>
            <div class="summary-value">{{ getSummaryDisplayAmount(summary.suggestSalaryTotal) }}</div>
          </div>
          <div class="summary-metric summary-metric-right">
            <div class="summary-title">总计结算薪资</div>
            <div class="summary-value">{{ getSummaryDisplayAmount(summary.settlementSalaryTotal) }}</div>
          </div>
        </div>
      </section>

      <section class="member-list">
        <article v-for="member in members" :key="member.memberId" class="member-card">
          <div class="member-header">
            <div>
              <div class="member-name">{{ member.memberName }}</div>
              <div class="member-id">({{ member.entQrcodeId }})</div>
            </div>
            <div class="member-amount-area">
              <div class="member-amount">{{ member.displayAmount }}</div>
              <div class="member-status">{{ member.statusText }}</div>
            </div>
          </div>

          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-label">基础薪资</div>
              <div class="metric-value tone-default">{{ formatCurrency(member.baseSalary) }}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">提成</div>
              <div class="metric-value tone-positive">{{ formatSignedCurrency(member.commissionSalary, true) }}</div>
            </div>
            <div class="metric-card">
              <div class="metric-label">异常扣减</div>
              <div class="metric-value tone-negative">{{ formatSignedCurrency(member.abnormalDeduction, false) }}</div>
            </div>
          </div>

          <div class="action-row" :class="{ 'two-actions': member.actions.length === 2 }">
            <button
              v-for="action in member.actions"
              :key="action.key"
              type="button"
              class="action-button"
              :class="action.variant"
              @click="handleAction(member, action.key)"
            >
              {{ action.label }}
            </button>
          </div>
        </article>

        <div v-if="!members.length" class="empty-card">暂无薪资数据</div>
      </section>
    </div>

    <div v-if="datePopupVisible" class="popup-mask popup-mask-bottom" @click.self="closeDatePopup">
      <div class="popup-panel date-popup-panel">
        <div class="popup-title">近7天日期选择</div>
        <button
          v-for="option in dateOptions"
          :key="option.value"
          type="button"
          class="date-option"
          :class="{ 'date-option-active': option.value === selectedDate }"
          @click="selectDate(option.value)"
        >
          <span>{{ option.label }}</span>
          <i v-if="option.value === selectedDate" class="el-icon-check"></i>
        </button>
      </div>
    </div>

    <div
      v-if="adjustPopup.visible"
      class="popup-mask"
      :class="{ 'popup-mask-keyboard': adjustKeyboardVisible }"
      @click.self="handleAdjustMaskClick"
    >
      <div
        ref="adjustPopupPanel"
        class="popup-panel adjust-popup-panel"
        @click.stop
        @touchstart.stop
        @touchend.stop
      >
        <div class="popup-title">调整薪资</div>
        <div class="popup-notice">薪资请一次性核定准确，提交后无法修改调整</div>
        <div class="popup-section">
          <div class="popup-member">{{ adjustPopup.memberName }}</div>
          <div class="popup-member-sub">当前薪资：{{ adjustPopup.currentAmount }}</div>
        </div>
        <div class="popup-section">
          <label class="popup-label">调整后薪资</label>
          <van-field
            :value="adjustPopup.adjustSalary"
            class="popup-input"
            placeholder="请输入调整后薪资"
            type="text"
            inputmode="decimal"
            :min="0"
            @input="handleAdjustInput($event)"
          />
        </div>
        <div class="popup-section">
          <label class="popup-label">调整原因</label>
          <textarea
            v-model="adjustPopup.remark"
            class="popup-textarea"
            placeholder="请输入调整原因"
            maxlength="100"
          ></textarea>
        </div>
        <div class="popup-footer">
          <button type="button" class="popup-cancel" @click="closeAdjustPopup">取消</button>
          <button type="button" class="popup-submit" @click="submitAdjust">调整并确认薪资</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import dayjs from 'dayjs';
import { Toast, Field, Dialog } from 'vant';
import { handleBuryPoint } from '@/utils/tool.js';
import {
  reqSalaryAdjust,
  reqSalaryConfirm,
  reqSalaryList,
} from '@/apis/serviceApi.js';

Vue.use(Toast);
Vue.use(Field);
Vue.use(Dialog);

const DEFAULT_PAGE_SIZE = 100;
const DATE_RANGE_DAYS = 7;
const SALARY_LIST_PAGE_EVENT = 'h5-sudaseeker_salaryListPage';

function getSalaryDateAnchor() {
  return dayjs().subtract(1, 'day').startOf('day');
}

function buildAllowedDateValues() {
  const anchor = getSalaryDateAnchor();
  return Array.from({ length: DATE_RANGE_DAYS }).map((_, index) => anchor.subtract(index, 'day').format('YYYY-MM-DD'));
}

function normalizeSalaryDate(value) {
  if (!value || !dayjs(value).isValid()) {
    return getSalaryDateAnchor().format('YYYY-MM-DD');
  }
  const currentDate = dayjs(value).startOf('day');
  const maxDate = getSalaryDateAnchor();
  const minDate = maxDate.subtract(DATE_RANGE_DAYS - 1, 'day');
  if (currentDate.isAfter(maxDate)) {
    return maxDate.format('YYYY-MM-DD');
  }
  if (currentDate.isBefore(minDate)) {
    return minDate.format('YYYY-MM-DD');
  }
  return currentDate.format('YYYY-MM-DD');
}

function buildRecentDateOptions() {
  const anchor = getSalaryDateAnchor();
  return Array.from({ length: DATE_RANGE_DAYS }).map((_, index) => {
    const date = anchor.subtract(index, 'day');
    return {
      value: date.format('YYYY-MM-DD'),
      label: date.format('YYYY年MM月DD日'),
    };
  });
}

function createDefaultAdjustPopup() {
  return {
    visible: false,
    memberId: '',
    salaryRecordId: '',
    memberName: '',
    currentAmount: '',
    adjustSalary: '',
    remark: '',
  };
}

export default {
  name: 'SalaryPage',
  data() {
    const initialDate = normalizeSalaryDate(this.$route.query.calcDate);
    return {
      cityName: this.$route.query.cityName || '--',
      teamName: this.$route.query.teamName || '--',
      selectedDate: initialDate,
      isAmountVisible: true,
      isLoading: false,
      summary: {
        suggestSalaryTotal: 0,
        settlementSalaryTotal: 0,
      },
      members: [],
      datePopupVisible: false,
      adjustPopup: createDefaultAdjustPopup(),
      bodyScrollTop: 0,
      adjustKeyboardVisible: false,
      adjustFocusLeaveTimer: null,
      adjustInitialViewportHeight: 0,
      adjustScrollTimer: null,
    };
  },
  computed: {
    dateOptions() {
      return buildRecentDateOptions();
    },
    currentDateLabel() {
      return dayjs(this.selectedDate).format('YYYY年MM月DD日');
    },
  },
  created() {
    this.trackBuryPoint('show', {
      groupId: this.$route.query.groupId || '',
      cityCode: this.$route.query.cityCode || '',
      calcDate: this.selectedDate,
      teamName: this.teamName || '',
    });
    if (this.$route.query.calcDate !== this.selectedDate) {
      this.syncRouteDate(this.selectedDate);
    }
    this.loadSalaryList();
  },
  mounted() {
    this.bindAdjustKeyboardListeners();
  },
  beforeDestroy() {
    this.unbindAdjustKeyboardListeners();
    this.unlockPageScroll();
  },
  watch: {
    $route(to, from) {
      this.cityName = to.query.cityName || '--';
      this.teamName = to.query.teamName || '--';
      const normalizedDate = normalizeSalaryDate(to.query.calcDate);
      if (to.query.calcDate !== normalizedDate) {
        this.selectedDate = normalizedDate;
        this.syncRouteDate(normalizedDate, true);
      } else {
        this.selectedDate = normalizedDate;
      }
      const nextLoadKey = [
        to.query.groupId || '',
        to.query.cityCode || '',
        normalizedDate,
      ].join('|');
      const previousLoadKey = [
        from.query.groupId || '',
        from.query.cityCode || '',
        normalizeSalaryDate(from.query.calcDate),
      ].join('|');
      if (nextLoadKey !== previousLoadKey) {
        this.loadSalaryList();
      }
    },
  },
  methods: {
    bindAdjustKeyboardListeners() {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return;
      }

      this.adjustInitialViewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      document.addEventListener('focusin', this.handleAdjustFocusIn, true);
      document.addEventListener('focusout', this.handleAdjustFocusOut, true);

      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', this.handleAdjustViewportResize);
      } else {
        window.addEventListener('resize', this.handleAdjustViewportResize);
      }
    },
    unbindAdjustKeyboardListeners() {
      if (typeof document !== 'undefined') {
        document.removeEventListener('focusin', this.handleAdjustFocusIn, true);
        document.removeEventListener('focusout', this.handleAdjustFocusOut, true);
      }

      if (typeof window !== 'undefined' && window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.handleAdjustViewportResize);
      } else if (typeof window !== 'undefined') {
        window.removeEventListener('resize', this.handleAdjustViewportResize);
      }

      if (this.adjustFocusLeaveTimer) {
        clearTimeout(this.adjustFocusLeaveTimer);
        this.adjustFocusLeaveTimer = null;
      }

      if (this.adjustScrollTimer) {
        clearTimeout(this.adjustScrollTimer);
        this.adjustScrollTimer = null;
      }
    },
    isEditableAdjustElement(target) {
      if (!target || !target.tagName) {
        return false;
      }

      const tagName = target.tagName.toLowerCase();
      if (!['input', 'textarea', 'select'].includes(tagName)) {
        return false;
      }

      return !target.readOnly && !target.disabled;
    },
    isTargetInsideAdjustPopup(target) {
      const panel = this.$refs.adjustPopupPanel;
      return !!(panel && target && panel.contains(target));
    },
    getActiveAdjustEditableElement() {
      if (typeof document === 'undefined') {
        return null;
      }

      const activeElement = document.activeElement;
      if (!this.isEditableAdjustElement(activeElement) || !this.isTargetInsideAdjustPopup(activeElement)) {
        return null;
      }

      return activeElement;
    },
    handleAdjustViewportResize() {
      if (typeof window === 'undefined') {
        return;
      }

      const viewportHeight = window.visualViewport
        ? window.visualViewport.height
        : window.innerHeight;

      if (!this.adjustInitialViewportHeight) {
        this.adjustInitialViewportHeight = viewportHeight;
        return;
      }

      const activeEditableElement = this.getActiveAdjustEditableElement();
      if (!activeEditableElement && viewportHeight >= this.adjustInitialViewportHeight) {
        this.adjustInitialViewportHeight = viewportHeight;
        this.adjustKeyboardVisible = false;
        return;
      }

      this.adjustKeyboardVisible = this.adjustPopup.visible && this.adjustInitialViewportHeight - viewportHeight > 120;

      if (this.adjustKeyboardVisible && activeEditableElement) {
        this.scrollAdjustFieldIntoView(activeEditableElement);
      }
    },
    handleAdjustFocusIn(event) {
      const target = event && event.target;
      if (!this.adjustPopup.visible || !this.isTargetInsideAdjustPopup(target) || !this.isEditableAdjustElement(target)) {
        return;
      }

      if (this.adjustFocusLeaveTimer) {
        clearTimeout(this.adjustFocusLeaveTimer);
        this.adjustFocusLeaveTimer = null;
      }

      this.adjustKeyboardVisible = true;
      this.scrollAdjustFieldIntoView(target);
    },
    handleAdjustFocusOut(event) {
      const target = event && event.target;
      if (!this.adjustPopup.visible || !this.isTargetInsideAdjustPopup(target) || !this.isEditableAdjustElement(target)) {
        return;
      }

      if (this.adjustFocusLeaveTimer) {
        clearTimeout(this.adjustFocusLeaveTimer);
      }

      this.adjustFocusLeaveTimer = setTimeout(() => {
        const activeElement = this.getActiveAdjustEditableElement();
        if (!activeElement) {
          this.adjustKeyboardVisible = false;
        }
      }, 180);
    },
    scrollAdjustFieldIntoView(target) {
      if (!target) {
        return;
      }

      if (this.adjustScrollTimer) {
        clearTimeout(this.adjustScrollTimer);
        this.adjustScrollTimer = null;
      }

      window.requestAnimationFrame(() => {
        this.adjustScrollTimer = window.setTimeout(() => {
          const panel = this.$refs.adjustPopupPanel;
          if (!panel || typeof target.getBoundingClientRect !== 'function') {
            this.adjustScrollTimer = null;
            return;
          }

          try {
            const viewportHeight = typeof window !== 'undefined' && window.visualViewport
              ? window.visualViewport.height
              : window.innerHeight;
            const targetRect = target.getBoundingClientRect();
            const panelRect = panel.getBoundingClientRect();
            const safeTop = panelRect.top + 40;
            const safeBottom = Math.min(panelRect.bottom - 32, viewportHeight - 160);

            if (targetRect.bottom > safeBottom) {
              panel.scrollTop += targetRect.bottom - safeBottom + 24;
              this.adjustScrollTimer = null;
              return;
            }

            if (targetRect.top < safeTop) {
              panel.scrollTop = Math.max(panel.scrollTop - (safeTop - targetRect.top + 24), 0);
            }
          } catch (error) {
            if (typeof target.scrollIntoView === 'function') {
              target.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          }
          this.adjustScrollTimer = null;
        }, 260);
      });
    },
    trackBuryPoint(event, params) {
      handleBuryPoint(SALARY_LIST_PAGE_EVENT, event, params || {});
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return `¥ ${amount.toFixed(2)}`;
    },
    formatSignedCurrency(value, isPositive) {
      const amount = Math.abs(Number(value || 0));
      const prefix = isPositive ? '+' : '-';
      return `${prefix}¥ ${amount.toFixed(2)}`;
    },
    getSummaryDisplayAmount(value) {
      return this.isAmountVisible ? this.formatCurrency(value) : '¥ ****';
    },
    sanitizeMoneyInput(value) {
      const raw = value == null ? '' : String(value);
      let sanitized = raw.replace(/[^\d.]/g, '');
      const firstDotIndex = sanitized.indexOf('.');
      if (firstDotIndex > -1) {
        sanitized = sanitized.slice(0, firstDotIndex + 1) + sanitized.slice(firstDotIndex + 1).replace(/\./g, '');
      }
      if (sanitized.startsWith('.')) {
        sanitized = `0${sanitized}`;
      }
      const parts = sanitized.split('.');
      const integerPart = (parts[0] || '').replace(/^0+(?=\d)/, '') || (parts.length > 1 ? '0' : '');
      if (parts.length === 1) {
        return integerPart;
      }
      return `${integerPart || '0'}.${(parts[1] || '').slice(0, 2)}`;
    },
    openDatePopup() {
      this.trackBuryPoint('datePopup_click', {
        selectedDate: this.selectedDate,
      });
      this.lockPageScroll();
      this.datePopupVisible = true;
    },
    closeDatePopup() {
      this.datePopupVisible = false;
      this.unlockPageScroll();
    },
    selectDate(value) {
      const normalizedValue = normalizeSalaryDate(value);
      this.trackBuryPoint('dateSelect_click', {
        previousDate: this.selectedDate,
        nextDate: normalizedValue,
      });
      if (normalizedValue === this.selectedDate) {
        this.closeDatePopup();
        return;
      }
      this.selectedDate = normalizedValue;
      this.syncRouteDate(normalizedValue);
      this.closeDatePopup();
    },
    toggleAmountVisible() {
      this.trackBuryPoint('toggleAmountVisible_click', {
        nextVisible: !this.isAmountVisible,
      });
      this.isAmountVisible = !this.isAmountVisible;
    },
    getBaseQuery() {
      return {
        groupId: Number(this.$route.query.groupId || 0),
        cityCode: this.$route.query.cityCode || '',
      };
    },
    syncRouteDate(calcDate) {
      const nextDate = normalizeSalaryDate(calcDate);
      if (this.$route.query.calcDate === nextDate) {
        return;
      }
      const routePayload = {
        query: Object.assign({}, this.$route.query, {
          calcDate: nextDate,
        }),
      };
      const navigation = this.$router.replace(routePayload);
      navigation.catch((error) => {
        if (error && error.name !== 'NavigationDuplicated') {
          throw error;
        }
      });
    },
    mapMember(item) {
      const confirmStatus = Number(item.confirmStatus || 10);
      const displayAmount = confirmStatus === 20 ? item.settlementSalary : item.suggestSalary;
      const actions = [];
      if (confirmStatus === 10) {
        actions.push({ key: 'adjust', label: '调整薪资', variant: 'is-primary' });
      }
      actions.push({ key: 'detail', label: '薪资明细', variant: 'is-secondary' });
      if (confirmStatus !== 20) {
        actions.push({ key: 'confirm', label: '确认薪资', variant: 'is-secondary' });
      }
      return {
        memberId: item.memberId,
        salaryRecordId: item.id,
        memberName: item.memberName || '--',
        promotionInviteCode: item.promotionInviteCode || '',
        entQrcodeId: item.entQrcodeId || '',
        suggestSalary: Number(item.suggestSalary || 0),
        settlementSalary: Number(item.settlementSalary || 0),
        baseSalary: Number(item.baseSalary || 0),
        commissionSalary: Number(item.commissionSalary || 0),
        abnormalDeduction: Number(item.abnormalDeduction || 0),
        confirmStatus: confirmStatus,
        statusText: confirmStatus === 20 ? '结算薪资' : '建议薪资',
        displayAmount: this.formatCurrency(displayAmount),
        actions: actions,
      };
    },
    loadSalaryList() {
      const baseQuery = this.getBaseQuery();
      if (!baseQuery.groupId) {
        this.members = [];
        this.summary = {
          suggestSalaryTotal: 0,
          settlementSalaryTotal: 0,
        };
        return;
      }
      if (!baseQuery.cityCode) {
        this.members = [];
        this.summary = {
          suggestSalaryTotal: 0,
          settlementSalaryTotal: 0,
        };
        this.showToast('缺少城市编码，无法查询薪资');
        return;
      }

      this.isLoading = true;
      const params = {
        groupId: baseQuery.groupId,
        cityCode: baseQuery.cityCode,
        calcDate: this.selectedDate,
        pageNum: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      };
      this.trackBuryPoint('reqSalaryList_params', params);
      reqSalaryList(params)
        .then((res) => {
          if (res && res.code === 1) {
            const data = res.data || {};
            this.summary = {
              suggestSalaryTotal: Number(data.suggestSalaryTotal || 0),
              settlementSalaryTotal: Number(data.settlementSalaryTotal || 0),
            };
            this.members = (data.memberSalaryList || []).map((item) => this.mapMember(item));
            this.trackBuryPoint('reqSalaryList_success', {
              calcDate: this.selectedDate,
              memberCount: this.members.length,
              suggestSalaryTotal: this.summary.suggestSalaryTotal,
              settlementSalaryTotal: this.summary.settlementSalaryTotal,
            });
            return;
          }
          this.trackBuryPoint('reqSalaryList_err', {
            calcDate: this.selectedDate,
            code: res && res.code,
            msg: res && res.msg,
          });
          this.showToast('获取薪资列表失败');
        })
        .catch((error) => {
          this.trackBuryPoint('reqSalaryList_catch', {
            calcDate: this.selectedDate,
            errorMessage: error && error.message ? error.message : '',
          });
          this.showToast('获取薪资列表异常');
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    openScheme() {
      this.trackBuryPoint('openScheme_click', {
        calcDate: this.selectedDate,
        groupId: this.$route.query.groupId || '',
        cityCode: this.$route.query.cityCode || '',
      });
      this.$router.push({
        name: 'Commission',
        query: Object.assign({}, this.$route.query, {
          calcDate: this.selectedDate,
          preview: 'true',
        }),
      });
    },
    openAdjustPopup(member) {
      this.trackBuryPoint('openAdjustPopup_click', {
        memberId: member.memberId,
        salaryRecordId: member.salaryRecordId,
        confirmStatus: member.confirmStatus,
        calcDate: this.selectedDate,
      });
      this.adjustPopup = {
        visible: true,
        memberId: member.memberId,
        salaryRecordId: member.salaryRecordId,
        memberName: member.memberName,
        currentAmount: member.displayAmount,
        adjustSalary: '',
        remark: '',
      };
      this.$nextTick(() => {
        const activeElement = this.getActiveAdjustEditableElement();
        if (activeElement) {
          this.scrollAdjustFieldIntoView(activeElement);
        }
      });
    },
    handleAdjustMaskClick() {
      if (this.adjustKeyboardVisible || this.getActiveAdjustEditableElement()) {
        return;
      }
      this.closeAdjustPopup();
    },
    closeAdjustPopup() {
      this.adjustKeyboardVisible = false;
      this.adjustPopup = createDefaultAdjustPopup();
    },
    handleAdjustInput(value) {
      this.adjustPopup.adjustSalary = this.sanitizeMoneyInput(value);
    },
    submitAdjust() {
      const baseQuery = this.getBaseQuery();
      this.trackBuryPoint('submitAdjust_click', {
        memberId: this.adjustPopup.memberId,
        salaryRecordId: this.adjustPopup.salaryRecordId,
        calcDate: this.selectedDate,
      });
      if (!baseQuery.cityCode || !baseQuery.groupId) {
        this.trackBuryPoint('submitAdjust_blocked', {
          reason: 'missingBaseQuery',
          cityCode: baseQuery.cityCode,
          groupId: baseQuery.groupId,
        });
        this.showToast('缺少必要参数，无法调整薪资');
        return;
      }
      if (!this.adjustPopup.salaryRecordId) {
        this.trackBuryPoint('submitAdjust_blocked', {
          reason: 'missingSalaryRecordId',
          memberId: this.adjustPopup.memberId,
        });
        this.showToast('缺少薪资记录ID，暂无法调整薪资');
        return;
      }
      if (!this.adjustPopup.adjustSalary) {
        this.trackBuryPoint('submitAdjust_blocked', {
          reason: 'missingAdjustSalary',
          memberId: this.adjustPopup.memberId,
        });
        this.showToast('请输入调整后薪资');
        return;
      }

      if (!/^\d+(\.\d{1,2})?$/.test(this.adjustPopup.adjustSalary) || Number(this.adjustPopup.adjustSalary) < 0) {
        this.trackBuryPoint('submitAdjust_blocked', {
          reason: 'invalidAdjustSalary',
          memberId: this.adjustPopup.memberId,
          adjustSalary: this.adjustPopup.adjustSalary,
        });
        this.showToast('调整后薪资需为不小于0的数字，且最多保留两位小数');
        return;
      }

      if (!this.adjustPopup.remark.trim()) {
        this.trackBuryPoint('submitAdjust_blocked', {
          reason: 'missingRemark',
          memberId: this.adjustPopup.memberId,
        });
        this.showToast('请输入调整原因');
        return;
      }

      const params = {
        groupId: baseQuery.groupId,
        cityCode: baseQuery.cityCode,
        memberId: this.adjustPopup.memberId,
        salaryRecordId: this.adjustPopup.salaryRecordId,
        calcDate: this.selectedDate,
        adjustList: [
          {
            adjustSalary: Number(this.adjustPopup.adjustSalary),
            remark: this.adjustPopup.remark.trim(),
          },
        ],
      };
      this.trackBuryPoint('reqSalaryAdjust_params', params);
      reqSalaryAdjust(params)
        .then((res) => {
          if (res && res.code === 1) {
            this.trackBuryPoint('reqSalaryAdjust_success', {
              memberId: this.adjustPopup.memberId,
              salaryRecordId: this.adjustPopup.salaryRecordId,
              calcDate: this.selectedDate,
            });
            this.showToast('调整薪资成功');
            this.closeAdjustPopup();
            this.loadSalaryList();
            return;
          }
          this.trackBuryPoint('reqSalaryAdjust_err', {
            memberId: this.adjustPopup.memberId,
            salaryRecordId: this.adjustPopup.salaryRecordId,
            code: res && res.code,
            msg: res && res.msg,
          });
          this.showToast(res.msg || '调整薪资失败');
        })
        .catch((error) => {
          this.trackBuryPoint('reqSalaryAdjust_catch', {
            memberId: this.adjustPopup.memberId,
            salaryRecordId: this.adjustPopup.salaryRecordId,
            errorMessage: error && error.message ? error.message : '',
          });
          this.showToast('调整薪资异常');
        });
    },
    confirmSalary(member) {
      const baseQuery = this.getBaseQuery();
      this.trackBuryPoint('confirmSalary_click', {
        memberId: member.memberId,
        salaryRecordId: member.salaryRecordId,
        calcDate: this.selectedDate,
      });
      if (!baseQuery.groupId) {
        this.trackBuryPoint('confirmSalary_blocked', {
          reason: 'missingGroupId',
          memberId: member.memberId,
        });
        this.showToast('缺少团队ID，暂无法确认薪资');
        return;
      }
      if (!member.salaryRecordId) {
        this.trackBuryPoint('confirmSalary_blocked', {
          reason: 'missingSalaryRecordId',
          memberId: member.memberId,
        });
        this.showToast('缺少薪资记录ID，暂无法确认薪资');
        return;
      }
      // cityCode 
      if (!baseQuery.cityCode) {
        this.trackBuryPoint('confirmSalary_blocked', {
          reason: 'missingCityCode',
          memberId: member.memberId,
        });
        this.showToast('缺少城市编码，暂无法确认薪资');
        return;
      }
      // 成员id
      if (!member.memberId) {
        this.trackBuryPoint('confirmSalary_blocked', {
          reason: 'missingMemberId',
          salaryRecordId: member.salaryRecordId,
        });
        this.showToast('缺少成员ID，暂无法确认薪资');
        return;
      }
      const params = {
        groupId: baseQuery.groupId,
        salaryRecordId: member.salaryRecordId,
        cityCode: baseQuery.cityCode,
        memberId: member.memberId,
      };
      this.trackBuryPoint('reqSalaryConfirm_params', params);
      reqSalaryConfirm(params)
        .then((res) => {
          if (res && res.code === 1) {
            this.trackBuryPoint('reqSalaryConfirm_success', {
              memberId: member.memberId,
              salaryRecordId: member.salaryRecordId,
              calcDate: this.selectedDate,
            });
            this.showToast(`${member.memberName} 已确认薪资`);
            this.loadSalaryList();
            return;
          }
          this.trackBuryPoint('reqSalaryConfirm_err', {
            memberId: member.memberId,
            salaryRecordId: member.salaryRecordId,
            code: res && res.code,
            msg: res && res.msg,
          });
          this.showToast('确认薪资失败');
        })
        .catch((error) => {
          this.trackBuryPoint('reqSalaryConfirm_catch', {
            memberId: member.memberId,
            salaryRecordId: member.salaryRecordId,
            errorMessage: error && error.message ? error.message : '',
          });
          this.showToast('确认薪资异常');
        });
    },
    openDetail(member) {
      this.trackBuryPoint('openDetail_click', {
        memberId: member.memberId,
        salaryRecordId: member.salaryRecordId,
        calcDate: this.selectedDate,
      });
      this.$router.push({
        name: 'SalaryDetail',
        query: Object.assign({}, this.$route.query, {
          memberId: member.memberId,
          salaryRecordId: member.salaryRecordId,
          calcDate: this.selectedDate,
        }),
      });
    },
    handleAction(member, actionKey) {
      if (actionKey === 'detail') {
        this.openDetail(member);
        return;
      }
      if (actionKey === 'adjust') {
        this.openAdjustPopup(member);
        return;
      }
      if (actionKey === 'confirm') {
        // 二次确认  使用vant 的 Dialog 组件
        this.$dialog.confirm({
          title: '确认薪资',
          message: `确定要确认 ${member.memberName} 的薪资吗？`,
          confirmButtonText: '确认',
          cancelButtonText: '取消',
        })
          .then(() => {
            this.confirmSalary(member);
          })
          .catch(() => {
            // 取消操作
          });
      }
    },
    showToast(message) {
      Toast({
        message: message,
        duration: 1600,
      });
    },
    lockPageScroll() {
      if (typeof document === 'undefined') {
        return;
      }
      const body = document.body;
      if (!body || body.dataset.salaryScrollLocked === '1') {
        return;
      }
      this.bodyScrollTop = window.pageYOffset || document.documentElement.scrollTop || body.scrollTop || 0;
      body.dataset.salaryScrollLocked = '1';
      body.style.position = 'fixed';
      body.style.top = `-${this.bodyScrollTop}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    },
    unlockPageScroll() {
      if (typeof document === 'undefined') {
        return;
      }
      const body = document.body;
      if (!body || body.dataset.salaryScrollLocked !== '1') {
        return;
      }
      body.dataset.salaryScrollLocked = '0';
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, this.bodyScrollTop || 0);
    },
  },
};
</script>

<style scoped>
.salary-page {
  min-height: 100vh;
  background: #f7f8fa;
  padding: 34px 32px 88px;
  box-sizing: border-box;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif;
  color: #1c2742;
}

.hero-section,
.page-body {
  max-width: 880px;
  margin: 0 auto;
}

.page-meta {
  display: flex;
  align-items: center;
  gap: 44px;
  font-size: 26px;
  font-weight: 600;
  line-height: 40px;
  color: #373737;
  margin-bottom: 34px;
}

.meta-city,
.meta-team {
  font-size: 26px;
  line-height: 40px;
  font-weight: 600;
}

.page-body {
  margin-top: 26px;
}

.filter-card,
.member-card,
.summary-card,
.popup-panel {
  background: #ffffff;
  border-radius: 28px;
  box-shadow: 0 12px 32px rgba(36, 42, 70, 0.08);
}

.filter-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 28px 30px;
}

.filter-main {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  color: inherit;
}

.filter-date-line {
  display: inline-flex;
  align-items: center;
  gap: 18px;
}

.filter-date {
  font-size: 38px;
  line-height: 1.25;
  font-weight: 700;
  color: #1d2537;
}

.filter-tip {
  font-size: 26px;
  line-height: 1.3;
  color: #596277;
}

.filter-actions {
  flex-shrink: 0;
}

.scheme-button,
.summary-toggle,
.action-button,
.date-option,
.popup-submit,
.popup-cancel {
  border: none;
  outline: none;
  font-family: inherit;
}

.scheme-button {
  height: 76px;
  padding: 0 26px;
  border-radius: 18px;
  background: linear-gradient(180deg, #4d8af9 0%, #3675eb 100%);
  color: #ffffff;
  font-size: 26px;
  font-weight: 700;
}

.summary-card {
  margin-top: 20px;
  padding: 28px 30px 32px;
  background: linear-gradient(180deg, #4485f7 0%, #3b79ef 100%);
  color: #ffffff;
}

.summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.summary-row-top {
  margin-bottom: 28px;
}

.summary-label,
.summary-toggle,
.summary-date,
.summary-title {
  font-size: 26px;
  line-height: 1.35;
}

.summary-meta-inline {
  display: inline-flex;
  align-items: center;
  gap: 14px;
}

.summary-label,
.summary-title,
.summary-value {
  font-weight: 700;
}

.summary-date {
  color: rgba(255, 255, 255, 0.82);
}

.summary-toggle {
  background: transparent;
  color: #ffffff;
}

.summary-row-bottom {
  align-items: stretch;
}

.summary-metric {
  flex: 1;
}

.summary-metric-right {
  text-align: right;
}

.summary-value {
  margin-top: 16px;
  font-size: 38px;
  line-height: 1.15;
}

.member-list {
  margin-top: 22px;
}

.member-card {
  padding: 28px 30px 30px;
}

.member-card + .member-card {
  margin-top: 18px;
}

.member-header,
.action-row {
  display: flex;
  justify-content: space-between;
}

.member-header {
  align-items: flex-start;
  gap: 16px;
}

.member-header > div:first-child {
  min-width: 0;
}

.member-name,
.member-amount {
  font-size: 38px;
  line-height: 1.25;
  font-weight: 700;
  color: #1d2537;
}

.member-id,
.member-status,
.metric-label {
  margin-top: 8px;
  font-size: 26px;
  line-height: 1.35;
  color: #6e7b92;
}

.member-amount-area {
  text-align: right;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 22px;
}

.metric-card {
  padding: 18px 16px;
  border-radius: 20px;
  background: #f7f8fb;
}

.metric-value {
  margin-top: 14px;
  font-size: 26px;
  line-height: 1.25;
  font-weight: 700;
}

.tone-default {
  color: #1d2537;
}

.tone-positive {
  color: #0ba56b;
}

.tone-negative {
  color: #ec4a72;
}

.action-row {
  gap: 14px;
  margin-top: 24px;
  flex-wrap: nowrap;
}

.action-row.two-actions .action-button {
  flex: 1;
}

.action-button {
  flex: 1;
  height: 74px;
  border-radius: 16px;
  font-size: 26px;
  line-height: 1.2;
  font-weight: 700;
}

.action-button.is-primary {
  background: linear-gradient(90deg, #4168ef 0%, #4160ea 100%);
  color: #ffffff;
}

.action-button.is-secondary {
  background: #f7f8fb;
  color: #4168ef;
}

.empty-card {
  min-height: 220px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 22px;
  border-radius: 28px;
  background: #ffffff;
  font-size: 26px;
  font-weight: 700;
  color: #6f85ac;
}

.popup-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.45);
  overflow: hidden;
}

.popup-mask-keyboard {
  align-items: flex-start;
  overflow-y: auto;
  padding-top: 24px;
  padding-bottom: 24px;
}

.popup-mask-bottom {
  align-items: flex-end;
  padding: 0;
}

.popup-panel {
  width: min(880px, calc(100vw - 48px));
  padding: 38px 32px;
  box-sizing: border-box;
}

.date-popup-panel {
  width: 100%;
  max-width: 880px;
  max-height: 76vh;
  padding-bottom: calc(38px + env(safe-area-inset-bottom));
  border-radius: 32px 32px 0 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.adjust-popup-panel {
  max-width: 760px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

.popup-title {
  text-align: center;
  font-size: 38px;
  line-height: 1.2;
  font-weight: 700;
  color: #19233d;
  margin-bottom: 30px;
}

.popup-notice {
  margin: -8px 0 22px;
  font-size: 26px;
  line-height: 1.5;
  color: #dc5f00;
}

.date-option {
  width: 100%;
  height: 78px;
  padding: 0 18px;
  border-radius: 18px;
  background: #f7f8fb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #30405b;
  font-size: 26px;
  font-weight: 600;
}

.date-option + .date-option {
  margin-top: 14px;
}

.date-option-active {
  background: rgba(65, 104, 239, 0.12);
  color: #4168ef;
}

.popup-section + .popup-section {
  margin-top: 24px;
}

.popup-member {
  font-size: 38px;
  line-height: 1.3;
  font-weight: 700;
  color: #1d2537;
}

.popup-member-sub,
.popup-label {
  font-size: 26px;
  line-height: 1.4;
  color: #6e7b92;
}

.popup-member-sub {
  margin-top: 10px;
}

.popup-label {
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
}

.popup-input,
.popup-textarea {
  width: 100%;
  border: 1px solid #d5dceb;
  border-radius: 18px;
  padding: 20px 18px;
  font-size: 26px;
  line-height: 1.4;
  color: #1d2537;
  box-sizing: border-box;
}

.popup-textarea {
  min-height: 150px;
  resize: none;
}

.popup-footer {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-top: 28px;
}

.popup-cancel,
.popup-submit {
  height: 78px;
  border-radius: 16px;
  font-size: 26px;
  font-weight: 700;
}

.popup-cancel {
  background: #f3f4f8;
  color: #546179;
}

.popup-submit {
  background: linear-gradient(90deg, #4168ef 0%, #4160ea 100%);
  color: #ffffff;
}
</style>
